import * as Select from '@radix-ui/react-select';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ForwardedRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { chip } from '../../Chip/Chip';
import { Icon } from '../../Icon/Icon';
import { type InputBaseProps, inputBase } from '../base';

const dropdownTrigger = cva('flex items-center text-content-subdued w-full');

type DropdownTriggerProps = VariantProps<typeof dropdownTrigger> &
  InputBaseProps & {
    ref: ForwardedRef<HTMLButtonElement>;
    placeholder?: string;
    ariaLabel?: string;
    id?: string;
    variant?: 'default' | 'chip';
  };

/**
 * Responsible for toggling the dropdown menu.
 *
 * @argument placeholder - Placeholder text to display when no value is selected. Dropdown's value takes precedence.
 * @argument variant - Visual style of the trigger. 'chip' renders a compact pill-shaped trigger.
 */
export const DropdownTrigger = ({
  placeholder,
  ariaLabel,
  intent,
  id,
  variant = 'default',
  ref,
}: DropdownTriggerProps) => {
  const className =
    variant === 'chip'
      ? twMerge(
          chip(),
          'h-s flex-row whitespace-nowrap bg-surface hover:bg-surface-strong cursor-pointer',
        )
      : twMerge(dropdownTrigger(), inputBase({ intent }));

  return (
    <Select.Trigger
      aria-label={ariaLabel}
      className={className}
      id={id}
      ref={ref}
    >
      <Select.Value placeholder={placeholder} />
      <Select.Icon className="ml-auto">
        <Icon name="expand_more" />
      </Select.Icon>
    </Select.Trigger>
  );
};
